/**
 * Animated brand panel shown on the right side of the auth page.
 * Dark background with floating golden blobs, a spinning dashed ring,
 * a subtle dot-grid, and the product tagline.
 */
import { useTranslation } from 'react-i18next'

export function AuthBrandPanel() {
  const { t } = useTranslation('auth')

  return (
    <div className="relative hidden lg:flex flex-1 items-center justify-center overflow-hidden bg-gradient-to-br from-black via-[#0a0a0a] to-[#161616]">
      {/* Animated blobs */}
      <span
        className="absolute rounded-full bg-primary opacity-[0.12]"
        style={{
          width: 300,
          height: 300,
          top: '-10%',
          left: '12%',
          animation: 'blob-drift-1 11s ease-in-out infinite',
        }}
      />
      <span
        className="absolute rounded-full bg-primary opacity-[0.10]"
        style={{
          width: 200,
          height: 200,
          top: '52%',
          left: '0%',
          animation: 'blob-drift-2 15s ease-in-out infinite',
        }}
      />
      <span
        className="absolute rounded-full bg-primary opacity-[0.14]"
        style={{
          width: 150,
          height: 150,
          top: '2%',
          left: '58%',
          animation: 'blob-drift-3 10s ease-in-out infinite',
        }}
      />
      <span
        className="absolute rounded-full bg-primary opacity-[0.08]"
        style={{
          width: 100,
          height: 100,
          top: '65%',
          left: '64%',
          animation: 'blob-drift-1 17s ease-in-out infinite reverse',
        }}
      />
      <span
        className="absolute rounded-full bg-primary opacity-[0.25]"
        style={{
          width: 68,
          height: 68,
          top: '37%',
          left: '43%',
          animation: 'blob-drift-2 7s ease-in-out infinite',
        }}
      />

      {/* Spinning dashed ring */}
      <svg
        className="absolute opacity-[0.06]"
        style={{ width: 260, height: 260, animation: 'ring-spin 65s linear infinite' }}
        viewBox="0 0 260 260"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="130"
          cy="130"
          r="120"
          fill="none"
          stroke="var(--primary)"
          strokeWidth="1"
          strokeDasharray="36 20"
        />
      </svg>

      {/* Dot grid (top-left decoration) */}
      <svg
        className="absolute top-5 left-5 opacity-[0.07]"
        style={{ width: 88, height: 88 }}
        viewBox="0 0 88 88"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g fill="var(--primary)">
          {[0, 1, 2, 3].map((row) =>
            [0, 1, 2, 3].map((col) => (
              <circle key={`${row}-${col}`} cx={8 + col * 16} cy={8 + row * 16} r={2.2} />
            ))
          )}
        </g>
      </svg>

      {/* Tagline */}
      <div className="relative z-10 max-w-[380px] px-10">
        <p className="text-[32px] font-bold leading-[1.3] tracking-tight">
          <span className="text-white/40">{t('brand.taglineLead')}</span>
          <span className="text-[56px] leading-[1.1] text-primary">
            {t('brand.taglineHighlight')}
          </span>
          <br />
          <span className="text-white/40">{t('brand.taglineMiddle')}</span>
          <span className="text-white/50 italic font-normal underline underline-offset-[5px] decoration-white/[0.18]">
            {t('brand.taglineEmphasis')}
          </span>
          <span className="text-white/40">{t('brand.taglineEnd')}</span>
        </p>
      </div>

      {/* Security label */}
      <span className="absolute bottom-6 right-7 text-[8px] uppercase tracking-[0.2em] text-white/15">
        {t('brand.secureLogin')}
      </span>
    </div>
  )
}
